import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeployAuth } from "../context/DeployAuthContext";
import {
  FaTools,
  FaCog,
  FaEnvelope,
  FaUsers,
  FaHome as FaHomeIcon,
  FaChevronDown,
  FaChevronUp,
  FaHandshake,
  FaGlobe,
  FaShoppingCart,
  FaCalendarAlt,
  FaLaptopCode,
  FaQuestionCircle,
  FaCodeBranch,
  FaNewspaper
} from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from "../assets/RTSLOGO.png";
import LimeLogo from "../assets/lime-logo-no-bg.png";

// Shared desktop link — replaces eight near-identical blocks that each
// re-implemented the same active/hover/underline logic. `index` staggers the
// entrance so the bar assembles left-to-right instead of popping in at once.
//
// MUST live at module scope. Defined inside Navbar it would be a brand-new
// component type on every render, so React would unmount and remount the links
// on any state change — and opening a dropdown would replay this entrance
// animation on the whole bar.
const DesktopNavLink = ({ to, label, isActive, index, onSelect, activeColor, idleColor }) => (
  <button
    type="button"
    onClick={() => onSelect(to)}
    style={{ animationDelay: `${90 + index * 55}ms` }}
    className={`group relative whitespace-nowrap opacity-0 animate-nav-in py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${isActive ? activeColor : idleColor
      } hover:text-silver-light`}
  >
    {label}
    <span
      className={`absolute -bottom-0.5 left-0 block h-px w-full bg-moss origin-left transition-transform duration-300 ease-out ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
        }`}
    />
  </button>
);

const Navbar = () => {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useDeployAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isMobileServicesDropdownOpen, setIsMobileServicesDropdownOpen] = useState(false);
  const [isLimeliteDropdownOpen, setIsLimeliteDropdownOpen] = useState(false);
  const [isMobileLimeliteDropdownOpen, setIsMobileLimeliteDropdownOpen] = useState(false);
  const limeliteDropdownRef = useRef();
  const limeliteTimeoutRef = useRef();
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef();
  const servicesDropdownRef = useRef();
  const dropdownTimeoutRef = useRef();

  // Colors for consistent theming
  const primaryTextColor = "text-silver-light"; // Very light gray/silver
  const secondaryTextColor = "text-silver-medium"; // Medium gray/silver

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleMobileServicesDropdown = () => setIsMobileServicesDropdownOpen(!isMobileServicesDropdownOpen);

  // The chat launcher is fixed bottom-right at z-60; this bar is z-50. With the
  // mobile menu open the bubble sits on top of the lower menu rows and eats taps
  // meant for them, so it is told to stand down while the menu is open. An event
  // rather than shared state — the widget is mounted in App, not under Navbar.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('rts:mobile-menu', { detail: { open: isOpen } }));
  }, [isOpen]);

  useEffect(() => {
    // Check if user is on a services-related page and auto-expand mobile dropdown
    const isOnServicesPage = location.pathname === "/consulting" ||
      location.pathname === "/business-websites" ||
      location.pathname === "/booking-websites" ||
      location.pathname === "/limelite/projects" ||
      location.pathname === "/custom-development" ||
      location.pathname.startsWith("/services");

    if (isOnServicesPage) {
      setIsMobileServicesDropdownOpen(true);
    } else if (location.pathname === "/contact") {
      setIsMobileServicesDropdownOpen(false);
    }
  }, [location]);

  useEffect(() => {
    if (location.hash === "#contact-section") {
      const contactSection = document.getElementById("contact-section");
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        // Close main mobile menu on outside click if needed
        // setIsOpen(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
      if (limeliteDropdownRef.current && !limeliteDropdownRef.current.contains(event.target)) {
        setIsLimeliteDropdownOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
      // Clean up timeout on unmount
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
      if (limeliteTimeoutRef.current) {
        clearTimeout(limeliteTimeoutRef.current);
      }
    };
  }, []);

  const closeMenu = () => {
    setIsOpen(false);
    setIsMobileServicesDropdownOpen(false);
    setIsMobileLimeliteDropdownOpen(false);
  };

  const closeDropdowns = () => {
    setIsServicesDropdownOpen(false);
    setIsMobileServicesDropdownOpen(false);
  };

  const handleMouseEnterDropdown = () => {
    // Clear any pending timeout
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsServicesDropdownOpen(true);
  };

  const handleMouseLeaveDropdown = () => {
    // Add a delay before closing the dropdown
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsServicesDropdownOpen(false);
    }, 300); // 300ms delay
  };

  const handleMouseEnterLimelite = () => {
    if (limeliteTimeoutRef.current) {
      clearTimeout(limeliteTimeoutRef.current);
    }
    setIsLimeliteDropdownOpen(true);
  };

  const handleMouseLeaveLimelite = () => {
    limeliteTimeoutRef.current = setTimeout(() => {
      setIsLimeliteDropdownOpen(false);
    }, 300);
  };

  const handleNavLinkClick = (to) => {
    if (location.pathname === to) {
      window.scrollTo(0, 0);
    } else {
      navigate(to);
    }
    closeMenu();
    closeDropdowns();
  };

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo(0, 0);
    } else {
      navigate("/");
    }
    closeMenu();
    closeDropdowns();
  };

  const handleDeployLogout = async () => {
    await logout();
    navigate("/limelite");
    closeMenu();
    closeDropdowns();
  };

  // Updated NavLink for mobile
  const NavLinkWithClose = ({ to, children }) => (
    <div
      onClick={() => handleNavLinkClick(to)}
      className={`flex items-center pl-3 pr-4 py-2 border-l-4 ${location.pathname === to
        ? "border-moss text-silver-light bg-moss-night"
        : "border-transparent text-silver-medium hover:bg-gray-900 hover:border-silver-light"
        } text-base font-medium cursor-pointer`}
    >
      {children}
    </div>
  );

  // Services dropdown for mobile.
  //
  // The label and the chevron are two separate targets. They used to be one row
  // wired to the toggle, which meant /services was unreachable on a phone —
  // tapping the heading only expanded the list, and the desktop nav navigates on
  // that same click. Splitting them restores parity: label goes to the page,
  // chevron expands.
  const ServicesDropdownMobile = () => (
    <div>
      <div
        className={`flex items-stretch border-l-4 ${location.pathname.startsWith("/services") || location.pathname === "/consulting" || location.pathname === "/business-websites" || location.pathname === "/booking-websites" || location.pathname === "/limelite/projects" || location.pathname === "/custom-development"
          ? "border-moss text-silver-light bg-moss-night"
          : "border-transparent text-silver-medium"
          } text-base font-medium`}
      >
        <button
          type="button"
          onClick={() => handleNavLinkClick("/services")}
          className="flex flex-1 items-center pl-3 pr-2 py-2.5 text-left hover:bg-gray-900 hover:text-silver-light transition-colors"
        >
          <FaTools className="mr-2" />
          {t('navbar.services')}
        </button>
        <button
          type="button"
          onClick={toggleMobileServicesDropdown}
          aria-expanded={isMobileServicesDropdownOpen}
          aria-label={isMobileServicesDropdownOpen ? t('navbar.collapseMenu', 'Collapse services menu') : t('navbar.expandMenu', 'Expand services menu')}
          className="px-4 py-2.5 hover:bg-gray-900 hover:text-silver-light transition-colors"
        >
          {isMobileServicesDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>
      {isMobileServicesDropdownOpen && (
        <div className="pl-8 bg-gray-900">
          <div
            onClick={() => handleNavLinkClick("/custom-development")}
            className={`flex items-center pl-3 pr-4 py-2 border-l-4 ${location.pathname === "/custom-development"
              ? "border-moss text-silver-light bg-moss-night"
              : "border-transparent text-silver-medium hover:bg-gray-800 hover:border-silver-light"
              } text-sm font-medium cursor-pointer`}
          >
            <FaLaptopCode className="mr-2" />
            {t('navbar.customWebApp')}
          </div>
          <div
            onClick={() => handleNavLinkClick("/consulting")}
            className={`flex items-center pl-3 pr-4 py-2 border-l-4 ${location.pathname === "/consulting"
              ? "border-moss text-silver-light bg-moss-night"
              : "border-transparent text-silver-medium hover:bg-gray-800 hover:border-silver-light"
              } text-sm font-medium cursor-pointer`}
          >
            <FaHandshake className="mr-2" />
            {t('navbar.consulting')}
          </div>
          <div
            onClick={() => handleNavLinkClick("/limelite/projects")}
            className={`flex items-center pl-3 pr-4 py-2 border-l-4 ${location.pathname === "/limelite/projects"
              ? "border-moss text-silver-light bg-moss-night"
              : "border-transparent text-silver-medium hover:bg-gray-800 hover:border-silver-light"
              } text-sm font-medium cursor-pointer`}
          >
            <FaShoppingCart className="mr-2" />
            {t('navbar.limeliteEcommerce')}
          </div>
          <div
            onClick={() => handleNavLinkClick("/business-websites")}
            className={`flex items-center pl-3 pr-4 py-2 border-l-4 ${location.pathname === "/business-websites"
              ? "border-moss text-silver-light bg-moss-night"
              : "border-transparent text-silver-medium hover:bg-gray-800 hover:border-silver-light"
              } text-sm font-medium cursor-pointer`}
          >
            <FaGlobe className="mr-2" />
            {t('navbar.businessWebsite')}
          </div>
          <div
            onClick={() => handleNavLinkClick("/booking-websites")}
            className={`flex items-center pl-3 pr-4 py-2 border-l-4 ${location.pathname === "/booking-websites"
              ? "border-moss text-silver-light bg-moss-night"
              : "border-transparent text-silver-medium hover:bg-gray-800 hover:border-silver-light"
              } text-sm font-medium cursor-pointer`}
          >
            <FaCalendarAlt className="mr-2" />
            {t('navbar.booking')}
          </div>
        </div>
      )}
    </div>
  );

  // Simple links, in bar order. Services and Limelite stay bespoke below
  // because they own dropdowns.
  const simpleLinks = [
    { to: "/", label: t('navbar.home'), isActive: location.pathname === "/" },
    { to: "/solutions", label: t('navbar.solutions'), isActive: location.pathname.startsWith("/solutions") },
    { to: "/about", label: t('navbar.about'), isActive: location.pathname === "/about" },
    { to: "/open-source", label: t('navbar.openSource'), isActive: location.pathname === "/open-source" },
    { to: "/faq", label: t('navbar.faq'), isActive: location.pathname === "/faq" },
    { to: "/blog", label: t('navbar.blog', 'Blog'), isActive: location.pathname.startsWith("/blog") },
  ];

  return (
    <nav
      ref={navRef}
      className={`fixed w-full z-50 transition-all duration-500 ease-out ${isScrolled || isOpen
        ? "bg-black/85 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/40"
        : "bg-transparent border-b border-transparent"
        }`}
    >
      {/* At the top of the page the bar has no fill — instead a scrim that
          extends PAST the bar's own height, so it fades into the hero rather
          than ending in a hard horizontal band at the bar's edge. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-black/85 via-black/45 to-transparent transition-opacity duration-500 ${isScrolled || isOpen ? "opacity-0" : "opacity-100"
          }`}
      />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Bar shrinks on scroll — the page feels like it moves under a fixed chrome */}
        <div
          className={`flex justify-between items-center transition-all duration-500 ease-out ${isScrolled ? "h-16" : "h-20"
            }`}
        >
          {/* Left side: logo + stacked wordmark */}
          <div className="flex items-center">
            <button
              type="button"
              className="group flex-shrink-0 flex items-center cursor-pointer text-left"
              onClick={handleLogoClick}
              aria-label={t('navbar.companyName')}
            >
              <img
                src={Logo}
                alt=""
                aria-hidden="true"
                className={`mr-3 transition-transform duration-500 ease-out group-hover:scale-105 ${isScrolled ? "h-10 w-10" : "h-12 w-12"
                  }`}
              />
              {/* Stacked lockup reads as a considered wordmark and is far
                  narrower than one long line, which is what used to collide
                  with the nav links. */}
              <span className="block leading-none">
                <span className={`block text-lg font-semibold tracking-[0.2em] ${primaryTextColor}`}>
                  RIVIERA
                </span>
                <span className="block mt-1 text-[10px] font-medium tracking-[0.34em] text-silver-medium transition-colors duration-300 group-hover:text-moss-light">
                  TECH SOLUTIONS
                </span>
              </span>
            </button>
          </div>

          {/* Right side: Nav Links + Language Switcher (Desktop) */}
          <div className="hidden xl:flex xl:items-center space-x-5">
            {/* Desktop Nav Links Container */}
            <div className="flex items-center space-x-5">
              {/* Home */}
              <DesktopNavLink {...simpleLinks[0]} index={0} onSelect={handleNavLinkClick} activeColor={primaryTextColor} idleColor={secondaryTextColor} />

              {/* Services Link with Dropdown */}
              <div
                ref={servicesDropdownRef}
                className="relative"
                onMouseEnter={handleMouseEnterDropdown}
                onMouseLeave={handleMouseLeaveDropdown}
              >
                <button
                  type="button"
                  onClick={() => handleNavLinkClick("/services")}
                  style={{ animationDelay: "145ms" }}
                  className={`relative group inline-flex items-center whitespace-nowrap opacity-0 animate-nav-in py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${location.pathname.startsWith("/services") || location.pathname === "/consulting" || location.pathname === "/business-websites" || location.pathname === "/booking-websites" || location.pathname === "/limelite/projects" || location.pathname === "/custom-development" ? primaryTextColor : secondaryTextColor
                    } hover:text-silver-light`}
                >
                  {t('navbar.services')}
                  <FaChevronDown className={`ml-1.5 text-[10px] transition-transform duration-300 ${isServicesDropdownOpen ? 'rotate-180' : ''}`} />
                  <span className={`absolute -bottom-0.5 left-0 block h-px w-full bg-moss origin-left transition-transform duration-300 ease-out ${location.pathname.startsWith("/services") || location.pathname === "/consulting" || location.pathname === "/business-websites" || location.pathname === "/booking-websites" || location.pathname === "/limelite/projects" || location.pathname === "/custom-development" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}></span>
                </button>

                {/* Services Dropdown Menu */}
                {isServicesDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 origin-top animate-dropdown-in bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl shadow-black/60 z-50 overflow-hidden">
                    <div className="py-1">
                      <div
                        onClick={() => handleNavLinkClick("/custom-development")}
                        className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors duration-200 ${location.pathname === "/custom-development"
                          ? "bg-gray-900 text-silver-light"
                          : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"
                          }`}
                      >
                        <FaLaptopCode className="mr-2" />
                        {t('navbar.customWebApp')}
                      </div>
                      <div
                        onClick={() => handleNavLinkClick("/consulting")}
                        className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors duration-200 ${location.pathname === "/consulting"
                          ? "bg-gray-900 text-silver-light"
                          : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"
                          }`}
                      >
                        <FaHandshake className="mr-2" />
                        {t('navbar.consulting')}
                      </div>
                      <div
                        onClick={() => handleNavLinkClick("/limelite/projects")}
                        className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors duration-200 ${location.pathname === "/limelite/projects"
                          ? "bg-gray-900 text-silver-light"
                          : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"
                          }`}
                      >
                        <FaShoppingCart className="mr-2" />
                        {t('navbar.limeliteEcommerce')}
                      </div>
                      <div
                        onClick={() => handleNavLinkClick("/business-websites")}
                        className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors duration-200 ${location.pathname === "/business-websites"
                          ? "bg-gray-900 text-silver-light"
                          : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"
                          }`}
                      >
                        <FaGlobe className="mr-2" />
                        {t('navbar.businessWebsite')}
                      </div>
                      <div
                        onClick={() => handleNavLinkClick("/booking-websites")}
                        className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors duration-200 ${location.pathname === "/booking-websites"
                          ? "bg-gray-900 text-silver-light"
                          : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"
                          }`}
                      >
                        <FaCalendarAlt className="mr-2" />
                        {t('navbar.booking')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Solutions */}
              <DesktopNavLink {...simpleLinks[1]} index={2} onSelect={handleNavLinkClick} activeColor={primaryTextColor} idleColor={secondaryTextColor} />

              {/* Limelite dropdown */}
              <div
                ref={limeliteDropdownRef}
                className="relative"
                onFocus={handleMouseEnterLimelite}
                onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setIsLimeliteDropdownOpen(false); }}
                onMouseEnter={handleMouseEnterLimelite}
                onMouseLeave={handleMouseLeaveLimelite}
              >
                <button
                  type="button"
                  onClick={() => handleNavLinkClick("/limelite")}
                  style={{ animationDelay: "255ms" }}
                  className={`relative group inline-flex items-center whitespace-nowrap opacity-0 animate-nav-in py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${location.pathname.startsWith("/limelite") ? "text-lime-400" : "text-lime-600"} hover:text-lime-400`}
                >
                  <img src={LimeLogo} alt="" aria-hidden="true" className="w-4 h-4 mr-1.5 transition-transform duration-300 group-hover:scale-110" />
                  Limelite
                  <FaChevronDown className={`ml-1.5 text-[10px] transition-transform duration-300 ${isLimeliteDropdownOpen ? "rotate-180" : ""}`} />
                  <span className={`absolute -bottom-0.5 left-0 block h-px w-full bg-lime-500 origin-left transition-transform duration-300 ease-out ${location.pathname.startsWith("/limelite") ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                </button>
                {isLimeliteDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 origin-top animate-dropdown-in bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl shadow-black/60 z-50 overflow-hidden">
                    <div className="py-1">
                      {isAuthenticated && (
                        <div onClick={() => handleNavLinkClick("/limelite/deploy/dashboard")} className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors ${location.pathname === "/limelite/deploy/dashboard" ? "bg-gray-900 text-lime-400" : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"}`}>Dashboard</div>
                      )}
                      <button type="button" onClick={() => handleNavLinkClick("/limelite/marketplace")}  className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors ${location.pathname.startsWith("/limelite/marketplace") ? "bg-gray-900 text-lime-400" : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"}`}>Marketplace</button>
                      {isAuthenticated ? (
                        <>
                          <div onClick={() => handleNavLinkClick("/limelite/account")} className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors ${location.pathname.startsWith("/limelite/account") ? "bg-gray-900 text-lime-400" : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"}`}>Account</div>
                          <div onClick={handleDeployLogout} className="flex items-center px-4 py-2 text-sm cursor-pointer text-silver-medium transition-colors hover:bg-gray-900 hover:text-silver-light">Log out</div>
                        </>
                      ) : (
                        <div onClick={() => handleNavLinkClick("/limelite/deploy/login")} className="flex items-center px-4 py-2 text-sm cursor-pointer text-silver-medium transition-colors hover:bg-gray-900 hover:text-silver-light">Log in</div>
                      )}
                      {!isAuthenticated && <button type="button" onClick={() => handleNavLinkClick("/limelite/deploy/login?mode=register&project=ecommerce")} className="flex w-full px-4 py-2 text-sm font-semibold text-lime-400 hover:bg-gray-900">Start free</button>}
                      <button type="button" onClick={() => handleNavLinkClick("/limelite/projects")} className="flex w-full px-4 py-2 text-sm text-silver-medium hover:bg-gray-900">Browse projects</button>
                      <div onClick={() => handleNavLinkClick("/limelite/deploy/docs")} className={`flex items-center px-4 py-2 text-sm cursor-pointer transition-colors ${location.pathname === "/limelite/deploy/docs" ? "bg-gray-900 text-lime-400" : "text-silver-medium hover:bg-gray-900 hover:text-silver-light"}`}>
                        Docs
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* About · Open Source · FAQ · Blog */}
              {simpleLinks.slice(2).map((link, i) => (
                <DesktopNavLink key={link.to} {...link} index={i + 4} onSelect={handleNavLinkClick} activeColor={primaryTextColor} idleColor={secondaryTextColor} />
              ))}

              {/* Contact as a CTA — gives the bar a clear endpoint and a single
                  obvious action instead of nine equal-weight links. */}
              <button
                type="button"
                onClick={() => handleNavLinkClick(location.pathname.startsWith("/limelite") && !isAuthenticated ? "/limelite/deploy/login?mode=register&project=ecommerce" : "/contact")}
                style={{ animationDelay: "530ms" }}
                className={`opacity-0 animate-nav-in rounded-md border px-4 py-2 text-sm font-medium tracking-wide transition-all duration-300 ${location.pathname === "/contact"
                  ? "border-moss bg-moss text-black"
                  : "border-moss/60 text-silver-light hover:border-moss hover:bg-moss hover:text-black"
                  }`}
              >
                {location.pathname.startsWith("/limelite") && !isAuthenticated ? "Start free" : t("navbar.contact")}
              </button>
            </div>

            {/* Language Switcher Only */}
            <div className="flex items-center">
              <LanguageSwitcher />
            </div>
          </div>

          {/* Mobile Menu Button Area */}
          <div className="-mr-2 flex items-center xl:hidden">
            <div className="mr-2">
              <LanguageSwitcher onSwitch={closeMenu} />
            </div>
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className={`inline-flex items-center justify-center p-2 rounded-md ${secondaryTextColor} hover:${primaryTextColor} focus:outline-none`}
              aria-label="Main menu"
              aria-expanded={isOpen}
            >
              {/* Hamburger/Close Icon */}
              <svg
                className={`h-6 w-6 transition-transform transform ${isOpen ? "rotate-45" : "rotate-0"}`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`xl:hidden bg-off-black ${isOpen ? "block shadow-lg border-t border-gray-800" : "hidden"
          } transition-all duration-300 ease-in-out`}
      >
        <div className="pt-2 pb-3 space-y-1">
          <NavLinkWithClose to="/">
            <FaHomeIcon className="mr-2" />
            {t('navbar.home')}
          </NavLinkWithClose>
          <ServicesDropdownMobile />
          <NavLinkWithClose to="/solutions">
            <FaCog className="mr-2" />
            {t('navbar.solutions')}
          </NavLinkWithClose>
          {/* Mobile Limelite expandable */}
          <div>
            {/* Same split as Services above — the heading reaches /limelite, the
                chevron expands. Leaving this one toggle-only would read as a bug
                the moment Services starts navigating. */}
            <div
              className={`flex items-stretch border-l-4 ${location.pathname.startsWith("/limelite") ? "border-lime-500 text-lime-400 bg-gray-900" : "border-transparent text-lime-600"} text-base font-medium`}
            >
              <button
                type="button"
                onClick={() => handleNavLinkClick("/limelite")}
                className="flex flex-1 items-center pl-3 pr-2 py-2.5 text-left hover:bg-gray-900 hover:text-lime-400 transition-colors"
              >
                <img src={LimeLogo} alt="" className="w-4 h-4 mr-2" />
                Limelite
              </button>
              <button
                type="button"
                onClick={() => setIsMobileLimeliteDropdownOpen(!isMobileLimeliteDropdownOpen)}
                aria-expanded={isMobileLimeliteDropdownOpen}
                aria-label={isMobileLimeliteDropdownOpen ? 'Collapse Limelite menu' : 'Expand Limelite menu'}
                className="px-4 py-2.5 hover:bg-gray-900 hover:text-lime-400 transition-colors"
              >
                {isMobileLimeliteDropdownOpen ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
              </button>
            </div>
            {isMobileLimeliteDropdownOpen && (
              <div className="pl-6 bg-gray-900">
                {isAuthenticated && <div onClick={() => handleNavLinkClick("/limelite/deploy/dashboard")} className="flex items-center pl-3 pr-4 py-2 text-sm text-silver-medium hover:text-silver-light cursor-pointer border-l-4 border-transparent hover:border-lime-500">Dashboard</div>}
                <button type="button" onClick={() => handleNavLinkClick("/limelite/marketplace")}  className="flex items-center pl-3 pr-4 py-2 text-sm text-silver-medium hover:text-silver-light cursor-pointer border-l-4 border-transparent hover:border-lime-500">Marketplace</button>
                {isAuthenticated ? (
                  <>
                    <div onClick={() => handleNavLinkClick("/limelite/account")} className="flex items-center pl-3 pr-4 py-2 text-sm text-silver-medium hover:text-silver-light cursor-pointer border-l-4 border-transparent hover:border-lime-500">Account</div>
                    <div onClick={handleDeployLogout} className="flex items-center pl-3 pr-4 py-2 text-sm text-silver-medium hover:text-silver-light cursor-pointer border-l-4 border-transparent hover:border-lime-500">Log out</div>
                  </>
                ) : (
                  <div onClick={() => handleNavLinkClick("/limelite/deploy/login")} className="flex items-center pl-3 pr-4 py-2 text-sm text-silver-medium hover:text-silver-light cursor-pointer border-l-4 border-transparent hover:border-lime-500">Log in</div>
                )}
                {!isAuthenticated && <button type="button" onClick={() => handleNavLinkClick("/limelite/deploy/login?mode=register&project=ecommerce")} className="flex w-full pl-3 pr-4 py-3 text-sm font-semibold text-lime-400">Start free</button>}
                <button type="button" onClick={() => handleNavLinkClick("/limelite/projects")} className="flex w-full pl-3 pr-4 py-3 text-sm text-silver-medium">Browse projects</button>
                <div onClick={() => handleNavLinkClick("/limelite/deploy/docs")} className="flex items-center pl-3 pr-4 py-2 text-sm text-silver-medium hover:text-silver-light cursor-pointer border-l-4 border-transparent hover:border-lime-500">Docs</div>
              </div>
            )}
          </div>
          <NavLinkWithClose to="/about">
            <FaUsers className="mr-2" />
            {t('navbar.about')}
          </NavLinkWithClose>
          <NavLinkWithClose to="/open-source">
            <FaCodeBranch className="mr-2" />
            {t('navbar.openSource')}
          </NavLinkWithClose>
          <NavLinkWithClose to="/faq">
            <FaQuestionCircle className="mr-2" />
            {t('navbar.faq')}
          </NavLinkWithClose>
          <NavLinkWithClose to="/blog">
            <FaNewspaper className="mr-2" />
            {t('navbar.blog', 'Blog')}
          </NavLinkWithClose>
          <NavLinkWithClose to="/contact">
            <FaEnvelope className="mr-2" />
            {t('navbar.contact')}
          </NavLinkWithClose>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
